import { useEffect, useState } from "react";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/api";
import { FeedbackProvider } from "@/components/Feedback";
import { Ticker } from "@/components/Ticker";
import { FestivalBanner } from "@/components/FestivalBanner";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { Collections } from "@/components/Collections";
import { Rates } from "@/components/Rates";
import { SavingsScheme } from "@/components/SavingsScheme";
import { Manifesto } from "@/components/Manifesto";
import { TrustBadges } from "@/components/TrustBadges";
import { ContactSection } from "@/components/ContactSection";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";

export default function Home() {
  const [rates, setRates] = useState(null);
  const [intro, setIntro] = useState(true);
  const [banner, setBanner] = useState(null);
  const [bannerClosed, setBannerClosed] = useState(false);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    api.get("/rates").then((res) => setRates(res.data)).catch(() => {});
    api
      .get("/banner")
      .then((res) => {
        setBanner(res.data);
        setBannerClosed(sessionStorage.getItem("aj_banner_closed") === res.data.text);
      })
      .catch(() => {});
    const timer = setTimeout(() => setIntro(false), 2400);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      clearTimeout(timer);
    };
  }, []);

  const bannerOn = Boolean(banner?.enabled && banner.text && !bannerClosed);
  const closeBanner = () => {
    sessionStorage.setItem("aj_banner_closed", banner.text);
    setBannerClosed(true);
  };

  return (
    <FeedbackProvider>
      <div data-testid="home-page" className="min-h-screen bg-[#060B18] font-sans text-white antialiased">
        <AnimatePresence>
          {intro && (
            <motion.div
              data-testid="intro-splash"
              className="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-7 bg-[#060B18]"
              exit={{ opacity: 0, transition: { duration: 0.9, ease: "easeInOut" } }}
            >
              <motion.img
                src="/assets/logo-full.png"
                alt="Aradhana Jewellers"
                className="w-32 rounded-2xl border border-[#D4AF37]/25 md:w-40"
                initial={{ opacity: 0, scale: 0.85, filter: "blur(12px)" }}
                animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              />
              <motion.p
                className="font-mono text-[10px] tracking-[0.5em] text-[#99732B]"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 1 }}
              >
                SHREE ARADHANA JEWELLERS
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grain" />
        <AnimatePresence>{bannerOn && <FestivalBanner banner={banner} onClose={closeBanner} />}</AnimatePresence>
        <Ticker rates={rates} top={bannerOn ? 40 : 0} />
        <Navbar top={bannerOn ? 76 : 36} />
        <main>
          <Hero rates={rates} />
          <Marquee />
          <Collections />
          <Rates rates={rates} />
          <SavingsScheme rates={rates} />
          <Manifesto />
          <TrustBadges />
          <ContactSection />
        </main>
        <Footer />
        <WhatsAppFloat />
      </div>
    </FeedbackProvider>
  );
}
